import { useState, useMemo } from 'react';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Modal from '@components/Modal/Modal';
import {
  useRepairPartsSearch,
  searchByKeyword
} from '@api/enterprise/repairParts';
import useOnlineStatus from '@hooks/useOnlineStatus';
import { formatCurrency } from '@utils/numberFormatters';
import { useDebounce } from '../../hooks/useDebounce';
import { usePartsFilter } from '../../hooks/usePartsFilter';
import { usePartSelection } from '../../hooks/usePartSelection';
import { PartSearchField } from './PartSearchField';
import { ExpandCollapseControls } from './ExpandCollapseControls';
import { VirtualizedPartsList } from './VirtualizedPartsList';
import type { WorkOrderPart } from '../../types/parts.types';

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  jobNumber: string;
  existingParts?: WorkOrderPart[];
};

const ONLINE_SELECT_FIELDS = [
  'partNumber',
  'partName',
  'description',
  'companyName',
  'cost'
];

export const AddPartModal = ({
  open,
  onClose,
  onSuccess,
  jobNumber,
  existingParts = []
}: Props) => {
  const { t } = useTranslation('parts');
  const isOnline = useOnlineStatus();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const hasOnlineSearchTerm = isOnline && debouncedSearchTerm.length >= 2;

  const { data, isLoading, error } = useRepairPartsSearch(
    isOnline
      ? {
          filter: hasOnlineSearchTerm
            ? searchByKeyword(debouncedSearchTerm)
            : undefined,
          select: ONLINE_SELECT_FIELDS,
          top: 50,
          orderBy: 'companyName asc, description asc'
        }
      : {
          orderBy: 'companyName asc, description asc'
        },
    isOnline ? open && hasOnlineSearchTerm : open
  );

  const {
    selectedParts,
    isAddingParts,
    handleTogglePart,
    handleQuantityChange,
    isPartSelected,
    getPartQuantity,
    handleAddParts,
    clearSelection
  } = usePartSelection({
    jobNumber,
    repairPartsData: data,
    onSuccess
  });

  const existingPartsSet = useMemo(
    () =>
      new Set(
        existingParts
          .filter(part => part.companyName && part.partNumber)
          .map(part => `${part.companyName}:${part.partNumber}`)
      ),
    [existingParts]
  );

  const filteredAndGroupedParts = usePartsFilter({
    data,
    existingPartsSet,
    searchTerm: isOnline ? '' : debouncedSearchTerm,
    expandedGroups
  });

  const handleToggleGroup = (company: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(company)) next.delete(company);
      else next.add(company);
      return next;
    });
  };

  const handleClose = () => {
    if (!isAddingParts) {
      clearSelection();
      setSearchTerm('');
      setExpandedGroups(new Set());
      onClose();
    }
  };

  const showEmptySearchPrompt = isOnline && debouncedSearchTerm.length < 2;
  const showLoading = isLoading && !showEmptySearchPrompt;
  const showContent = !showLoading && !error && !showEmptySearchPrompt;

  return (
    <Modal open={open} onClose={handleClose} maxWidth="md" width="700px">
      <Modal.Header title={t('addPartModal.title')} onClose={handleClose} />
      <Modal.Content>
        <Box sx={{ mb: 2 }}>
          <PartSearchField value={searchTerm} onChange={setSearchTerm} />
          {showContent && filteredAndGroupedParts.companies.length > 0 && (
            <ExpandCollapseControls
              onExpandAll={() =>
                setExpandedGroups(new Set(filteredAndGroupedParts.companies))
              }
              onCollapseAll={() => setExpandedGroups(new Set())}
            />
          )}
        </Box>

        {showEmptySearchPrompt && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 300
            }}
          >
            <Typography variant="body1" color="text.secondary">
              {t('addPartModal.typeToSearch')}
            </Typography>
          </Box>
        )}

        {showLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 300
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('addPartModal.errorLoading')}
          </Alert>
        )}

        {showContent && (
          <Box sx={{ height: 400, position: 'relative' }}>
            <VirtualizedPartsList
              filteredParts={filteredAndGroupedParts}
              expandedGroups={expandedGroups}
              selectedParts={selectedParts}
              searchTerm={searchTerm}
              onToggleGroup={handleToggleGroup}
              onTogglePart={handleTogglePart}
              onQuantityChange={handleQuantityChange}
              isPartSelected={isPartSelected}
              getPartQuantity={getPartQuantity}
            />
          </Box>
        )}

        {selectedParts.size > 0 && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              {t('addPartModal.selectedCount', { count: selectedParts.size })}
            </Typography>
            <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
              {Array.from(selectedParts.entries()).map(
                ([partKey, partData]) => {
                  const [companyName, partNumber] = partKey.split(':');
                  const total = (partData.cost || 0) * partData.quantity;
                  return (
                    <Box
                      key={partKey}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 0.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': { borderBottom: 'none' }
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {partNumber}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {partData.partName || companyName}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: 'flex', gap: 2, alignItems: 'center' }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Qty: {partData.quantity}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(total)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pt: 1,
                mt: 1,
                borderTop: '2px solid',
                borderColor: 'divider'
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                Total
              </Typography>
              <Typography variant="subtitle2" fontWeight={600}>
                {formatCurrency(
                  Array.from(selectedParts.values()).reduce(
                    (sum, part) => sum + (part.cost || 0) * part.quantity,
                    0
                  )
                )}
              </Typography>
            </Box>
          </Box>
        )}
      </Modal.Content>
      <Modal.Footer
        primaryButton={{
          text: t('addPartModal.add'),
          onClick: handleAddParts,
          disabled: selectedParts.size === 0 || isAddingParts,
          loading: isAddingParts
        }}
        secondaryButton={{
          text: t('addPartModal.cancel'),
          onClick: handleClose,
          disabled: isAddingParts
        }}
      />
    </Modal>
  );
};
